/**
 * Brand keys — the TypeScript twin of the `programs_brand_key_check` CHECK constraint.
 *
 * `programs.brand_key` is the only brand column in the schema. Everything else derives:
 *
 *     program.brand_key
 *       └─ enrollment  → its program's brand
 *           └─ invoice → its enrollment's program's brand
 *
 * A family's brand set is the union of its children's enrollments. Parents and students
 * carry no brand: brand describes what a family bought, not who they are.
 *
 * **Brand is not a security boundary.** No RLS policy references `brand_key`. Cross-brand
 * visibility is accepted; brand is presentation and reporting scope only.
 *
 * Deliberately no display names, senders, logos or theme tokens — those have no consumer
 * until a branded screen exists, and inventing their shape now would be guessing. This file
 * exists so the CHECK constraint has exactly one mirror in the app, not zero or three.
 *
 * When a brand is added: widen the CHECK in a patch and add the key here, in the same PR.
 */

export const BRAND_KEYS = ['bhcs', 'kaleido'] as const;

export type BrandKey = (typeof BRAND_KEYS)[number];

/**
 * The brand every program belongs to unless told otherwise. Matches the column's DB default,
 * so a row selected without `brand_key` and a row inserted without one agree.
 */
export const DEFAULT_BRAND_KEY: BrandKey = 'bhcs';

/**
 * Whether an arbitrary value is a known brand key. Admits exactly the set the CHECK
 * constraint admits, so a value that passes here will not be rejected by the database.
 */
export function isBrandKey(value: unknown): value is BrandKey {
    return typeof value === 'string' && (BRAND_KEYS as readonly string[]).includes(value);
}
